/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import { useWorkspaces, type WorkspaceRead, type WorkspaceRole } from './queries'

interface WorkspaceContextValue {
  workspaces: WorkspaceRead[]
  currentWorkspace: WorkspaceRead | null
  currentWorkspaceUuid: string | null
  currentRole: WorkspaceRole | null
  setCurrentWorkspace: (uuid: string) => void
  setCurrentWorkspaceUuid: (uuid: string) => void
  isLoading: boolean
  error: Error | null
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null)

export function WorkspaceContextProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, error } = useWorkspaces()
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null)
  const workspaces = useMemo(() => data ?? [], [data])
  const currentWorkspace = useMemo(() => {
    if (workspaces.length === 0) return null
    return workspaces.find((workspace) => workspace.uuid === selectedUuid) ?? workspaces[0] ?? null
  }, [selectedUuid, workspaces])

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      workspaces,
      currentWorkspace,
      currentWorkspaceUuid: currentWorkspace?.uuid ?? null,
      currentRole: currentWorkspace?.role ?? null,
      setCurrentWorkspace: setSelectedUuid,
      setCurrentWorkspaceUuid: setSelectedUuid,
      isLoading,
      error: error instanceof Error ? error : null,
    }),
    [currentWorkspace, error, isLoading, workspaces],
  )

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>
}

export const WorkspaceProvider = WorkspaceContextProvider

export function useCurrentWorkspace() {
  const ctx = useContext(WorkspaceContext)
  if (!ctx) {
    throw new Error('useCurrentWorkspace must be used inside WorkspaceContextProvider')
  }
  return ctx
}

export function useCurrentWorkspaceOrNull() {
  return useContext(WorkspaceContext)
}
